export interface ProjectCaseStudy {
  slug: string
  name: string
  title: string
  description: string
  liveUrl: string
  category: string
  headline: string
  introduction: string
  role: string
  stack: Array<string>
  brief: Array<string>
  principle: string
  journeys: Array<{
    title: string
    label: string
    body: string
    detail: string
  }>
  gallery: Array<{ file: string; alt: string; caption: string }>
  architecture: Array<{ title: string; tools: string; body: string }>
  decisions: Array<{
    title: string
    constraint: string
    body: string
    outcome: string
  }>
  delivered: Array<string>
  engineeringHighlights: Array<string>
  fit: string
  questions: Array<[string, string]>
}

export const projectCaseStudies: Record<string, ProjectCaseStudy> = {
  'indian-context': {
    slug: 'indian-context',
    name: 'IndianContext',
    title: 'IndianContext: Editorial CMS Development Case Study | Suriya',
    description:
      'An editorial website and custom CMS for drafts, publishing, media and content discovery. Explore the workflows and full-stack development behind IndianContext.',
    liveUrl: 'https://indiancontext.com',
    category: 'Editorial website & publishing platform',
    headline: 'A considered reading experience. A capable publishing system.',
    introduction:
      'I designed and built a publication about India with a custom editorial workspace for preparing articles, organising content and managing publication without changing page code.',
    role: 'Full-stack development · Editorial platform design',
    stack: [
      'React',
      'TanStack Start',
      'TypeScript',
      'CASL',
      'Drizzle ORM',
      'Cloudflare Workers',
      'D1',
      'R2',
    ],
    principle:
      'Make the reading feel simple. Give the editorial work somewhere structured to happen.',
    brief: [
      'I approached IndianContext as two connected products: a publication people want to read and a workspace an editorial team can use every day. I wanted readers to find a useful starting point quickly, while giving editors the structure to prepare, organise and publish substantial content without asking a developer to change a page.',
      'I built the content model around the full life of an article. Alongside the title and body, I included excerpts, reading time, a table of contents, FAQs, search metadata, publication state, scheduling and relationships to categories and series. Each field has a purpose in the editorial workflow and a clear place in the public experience.',
      'I connected that model to a React and TanStack Start application running on Cloudflare. My focus was continuity: the content an editor maintains should directly power the navigation, discovery and reading interface. I designed the public pages and management patterns together so the system remains coherent as the publication develops.',
    ],
    journeys: [
      {
        label: 'Reader experience',
        title: 'Start with context, not an endless feed.',
        body: 'I organised discovery around latest stories, sections, series, topics and a start-here page. Each entry point serves a different reading intent: finding something recent, exploring a subject or following a connected explanation. I gave the homepage a clear editorial hierarchy instead of treating it as an undifferentiated feed. I also designed its content states so the layout remains useful while the publication changes. Readers get a consistent way to orient themselves whether they arrive at the homepage or deep inside the site.',
        detail: 'Homepage → sections or series → a reading path',
      },
      {
        label: 'Editorial experience',
        title: 'Separate the story from its publication state.',
        body: 'I separated preparing an article from making it public. I built draft and article management alongside categories, tags, series and scheduled publishing, with explicit fields for status, publication time and scheduled time. That gives an editor somewhere to work through the content, shape its context and choose its release point. I kept those decisions in structured workflows rather than burying them in page code, so the editorial model remains understandable to the people maintaining the publication.',
        detail: 'Draft → editorial metadata → publication or schedule',
      },
      {
        label: 'Platform experience',
        title: 'Keep supporting work inside the same system.',
        body: 'I brought media management, reader feedback, roles, audit logs and content activity into the same administrative foundation. I also built scheduled-task tooling around the editorial model. My aim was to keep the work around an article connected to the article itself: the files it uses, the taxonomy it belongs to, the people who can change it and the publication state it carries.',
        detail: 'Content operations → permissions → supporting tools',
      },
    ],
    gallery: [
      {
        file: 'homepage',
        alt: 'IndianContext live editorial homepage with India in context headline, reading navigation and honest article empty state',
        caption:
          'The live editorial homepage I built around reading and discovery.',
      },
      {
        file: 'approach',
        alt: 'IndianContext about page describing its editorial purpose and approach',
        caption:
          'Editorial identity and approach — the context around the content.',
      },
    ],
    architecture: [
      {
        title: 'Reader-facing delivery',
        tools: 'React / TanStack Start',
        body: 'I use React and TanStack Start for server-rendered public routes, reading layouts and content discovery. I connect the presentation to structured editorial data so each story has one maintained content source.',
      },
      {
        title: 'Editorial administration',
        tools: 'CASL / Zod / TanStack',
        body: 'I built roles, an access-policy builder, schema-driven forms and filterable management views. I use CASL-backed permissions, an access manifest generated at build time and server-side checks on every module, so what the interface offers matches what an editor can actually do.',
      },
      {
        title: 'Content & infrastructure',
        tools: 'Drizzle / D1 / R2 / Workers',
        body: 'I deploy the application on Cloudflare Workers with D1 for relational content and R2 for uploads. Public pages are cached against a single revision counter that falls back to uncached rendering if the lookup fails, and side effects such as notifications go through a transactional outbox with idempotent handlers and capped exponential backoff.',
      },
    ],
    decisions: [
      {
        title: 'Content deserves a model.',
        constraint: 'Beyond a title and body',
        body: 'I modelled categories, tags and ordered series separately because they answer different discovery questions. I use excerpts for scanning, a full body and table of contents for reading, and publication fields for editorial state. Making those distinctions explicit gives me a strong foundation for reusable forms and public views without losing the intent behind the content.',
        outcome:
          'Structured fields connect administration, discovery and reading.',
      },
      {
        title: 'Side effects need a delivery guarantee.',
        constraint: 'Work that must not be lost or repeated',
        body: 'I record events in an outbox table in the same transaction as the change, then process them in batches with a recorded set of handled events, capped exponential backoff and recovery for stale claims. The same reliability thinking applies to the public cache, which reads one revision row and degrades to uncached rendering rather than failing a page, and to rate limiting, which is stored in its own bucket table.',
        outcome:
          'Notifications and cache changes survive retries, restarts and partial failures.',
      },
      {
        title: 'Build discovery into the publishing system.',
        constraint: 'SEO as part of the content model',
        body: 'I gave articles dedicated metadata and stable slugs, then connected them to categories, topics and series. That makes discoverability part of the publishing workflow rather than a separate pass after writing. I paired those fields with server-rendered reading routes and clear navigation so the content structure serves both readers and search engines.',
        outcome:
          'Search-aware publishing with clear relationships between stories.',
      },
    ],
    delivered: [
      'I deployed IndianContext with dedicated reading, discovery and editorial-identity routes.',
      'I built dynamic article, draft, taxonomy and series workflows.',
      'I connected roles, media and reader feedback to the publishing workspace.',
      'I created a reusable React/TanStack administration foundation on Cloudflare, with 99 test files covering draft leakage, endpoint guards, slug conflicts, reader consent and public search.',
    ],
    engineeringHighlights: [
      'I keep the reader experience and editorial workspace connected through one structured content model.',
      'I use an outbox with idempotent handlers and a revision-based public cache that fails open, so side effects and cached pages stay consistent.',
      'I test the boundary between drafts and public routes so unpublished content never leaks, and I keep supporting tooling behind explicit permissions.',
    ],
    fit: 'I build publishing systems for editorial teams and businesses that need more than a blog theme. I start with how your content is organised, who prepares it and how it reaches readers, then shape the reading interface and administrative workflows around that process. IndianContext brings those concerns together in a working platform.',
    questions: [
      [
        'How does the editorial team manage publishing?',
        'I built administration for dynamic articles, drafts, categories, tags, series and media. Editors maintain the content and its publication state through the platform, while the public pages use those same structures to present and connect stories.',
      ],
      [
        'What technology powers the platform?',
        'I built IndianContext with React, TanStack Start, TypeScript, CASL and Drizzle. I use Cloudflare Workers for application delivery, D1 for relational content and R2 for uploaded media, and a transactional outbox and revision-based cache for reliability.',
      ],
      [
        'Can you build a similar platform for my publication?',
        'Yes. I can shape the content model, taxonomy, permissions and reading experience around a different publication. I would begin with its editorial process and audience needs, then adapt the workflows and interface instead of forcing the team into a generic blog template.',
      ],
    ],
  },
  'my-clinic': {
    slug: 'my-clinic',
    name: 'My Clinic',
    title: 'My Clinic: Dynamic Healthcare Website Case Study | Suriya',
    description:
      'A healthcare website case study covering dynamic services, doctor discovery and patient navigation. Explore the deployed My Clinic interface and design.',
    liveUrl: 'https://clinic.suriyaprakash.in',
    category: 'Dynamic healthcare website & service discovery',
    headline: 'Finding the right care should not begin with guessing.',
    introduction:
      'I built a dynamic healthcare website that connects departments, physicians and service options into a clear patient journey—from understanding the care to making contact.',
    role: 'Healthcare interface & connected content workflows',
    stack: [
      'React',
      'Dynamic Content',
      'Service Catalogues',
      'Patient Navigation',
    ],
    principle:
      'Help a visitor understand the options before asking them to choose one.',
    brief: [
      'I designed My Clinic around a familiar patient problem: knowing what help you need without knowing which department or appointment to choose. I wanted the website to make those options easier to understand, with useful information available before a visitor reaches a contact form.',
      'I connected departments, physician profiles and service pages into one public journey. Departments establish the broad structure, doctors give visitors a route into the care team, and services explain categories, durations and price variants. I treated those as related views of the clinic’s content rather than separate destinations competing for attention.',
      'I built the experience around dynamic content so the site can present a substantial catalogue without reducing it to a long, static list. My focus was the relationship between discoverability and clarity: a person should be able to enter through the homepage, a doctor profile or a service detail and still understand where to go next.',
    ],
    journeys: [
      {
        label: 'Orientation',
        title: 'Offer more than one way into the same care structure.',
        body: 'I created dedicated discovery routes for departments, doctors and services because visitors arrive with different levels of familiarity. Some know a department; others are looking for a specific clinician or consultation. I connected those routes through consistent navigation and clear homepage entry points. I also designed for visitors arriving directly on an internal page, where the interface needs to explain the wider care structure without forcing a return to the homepage. The result is a connected journey with several useful starting points.',
        detail:
          'Concern → department, doctor or service → relevant information',
      },
      {
        label: 'Service discovery',
        title: 'Explain the option, not just the name.',
        body: 'I built the services directory to explain the difference between options before asking a visitor to make contact. I surfaced categories, descriptions, durations, doctor-led indicators and variant-aware pricing at the comparison stage. A person can scan the directory, understand the broad choices and open the relevant detail. I gave variants a visible place in the interface because a short review and a more involved consultation should not become indistinguishable under one service name. That structure gives the catalogue more depth without overwhelming the first screen.',
        detail: 'Service category → options and variants → full detail',
      },
      {
        label: 'Making contact',
        title: 'Make the next step easy to find.',
        body: 'I placed contact and appointment-request paths alongside the information that helps someone use them. A visitor can explore a service, compare the care team and then ask for guidance without searching for a disconnected contact page. I kept the public journey focused on understanding and requesting care, with clear transitions from discovery to contact. That gives the clinic a consistent place to begin the conversation and gives patients a practical action at the point where they have enough context to take it.',
        detail: 'Understand the care → request guidance → clinic confirmation',
      },
    ],
    gallery: [
      {
        file: 'homepage',
        alt: 'My Clinic deployed healthcare homepage with departments, doctor discovery and appointment request navigation',
        caption:
          'The healthcare homepage I built around clear routes into care.',
      },
      {
        file: 'services',
        alt: 'My Clinic services directory showing category filters, durations, service variants and sample price ranges',
        caption:
          'Dynamic service discovery — catalogue structure, variants and pricing presentation.',
      },
    ],
    architecture: [
      {
        title: 'Public information architecture',
        tools: 'Homepage / departments / doctors',
        body: 'I organised the public site around departments, doctors and services. I connect those views through consistent navigation so a visitor can follow the route that matches their question.',
      },
      {
        title: 'Dynamic catalogue presentation',
        tools: 'Categories / service variants / durations',
        body: 'I use structured categories, variants, durations and prices to create useful service comparisons. I keep short discovery information and deeper service details at the appropriate levels of the journey.',
      },
      {
        title: 'Connected contact paths',
        tools: 'Service discovery / profiles / enquiries',
        body: 'I connect content pages to contact and appointment-request paths. I design each transition around the information a person has just read, keeping the next action relevant and easy to find.',
      },
    ],
    decisions: [
      {
        title: 'Lead with the patient’s question.',
        constraint: 'Not the database navigation',
        body: 'I treated departments, doctors and services as different ways of answering “Where should I go next?” I gave each a dedicated public route and made their relationship clear through navigation and page structure. That lets me support different levels of familiarity without forcing everyone through the same sequence of screens.',
        outcome:
          'Multiple discovery paths, with a consistent route toward contact.',
      },
      {
        title: 'Make service variation visible.',
        constraint: 'Clarity before a conversion',
        body: 'I surfaced variants, durations and price ranges where people compare care options. I wanted the directory to answer useful questions, with the detail page adding depth rather than simply repeating the same card. I use visual hierarchy to distinguish service names, explanatory copy and practical information so a richer catalogue remains readable.',
        outcome:
          'A richer comparison experience than a flat list of service names.',
      },
      {
        title: 'Build a consistent content experience.',
        constraint: 'One connected healthcare site',
        body: 'I brought care-team profiles, departments, services and health articles into a consistent page system. I keep the visual language familiar as someone moves between those areas, while giving each type of content the structure it needs. That balance lets the site feel cohesive without reducing every page to the same generic template.',
        outcome: 'A cohesive interface with purpose-built content structures.',
      },
    ],
    delivered: [
      'I deployed a connected healthcare website at clinic.suriyaprakash.in.',
      'I built discovery routes for departments, physicians, services and health articles.',
      'I designed a service directory with categories, variants, durations and price ranges.',
      'I connected care information with relevant contact and appointment-request paths.',
    ],
    engineeringHighlights: [
      'I organise dynamic healthcare content around the way people look for care.',
      'I give practical service information a visible place in the comparison experience.',
      'I use a consistent page system to connect discovery, detail and contact.',
    ],
    fit: 'I build healthcare websites around the questions patients bring and the content a clinic needs to maintain. I can shape the service catalogue, physician discovery, visual identity and enquiry journey around your practice, with a clear structure for keeping that information current.',
    questions: [
      [
        'How are services, doctors and departments connected?',
        'I gave services, physicians and departments their own connected page structures. That lets me present each type of information in the right format while keeping the wider care journey consistent across the site.',
      ],
      [
        'How do service variants work in the interface?',
        'I show variants, durations and price ranges alongside service descriptions so people can compare options before opening a detail page. I use categories and a clear visual hierarchy to keep that additional information easy to scan.',
      ],
      [
        'Can you build a website for my clinic?',
        'Yes. I can adapt the branding, care structure, team profiles and enquiry experience around a clinic’s requirements. I start by understanding how patients choose care and how the team maintains its information, then build the interface around those needs.',
      ],
    ],
  },
  'packcraft-india': {
    slug: 'packcraft-india',
    name: 'PackCraft India',
    title: 'PackCraft India: 3D Manufacturing Website Case Study | Suriya',
    description:
      'A manufacturing website with interactive 3D packaging, product comparison and a frontend quote prototype. Explore the design and development case study.',
    liveUrl: 'https://carton.suriyaprakash.in',
    category: 'Interactive manufacturing website & 3D storytelling',
    headline: 'Give an ordinary carton an extraordinary first impression.',
    introduction:
      'I designed and built a packaging website that pairs an interactive 3D carton with clear product comparisons and process information. The quote interface is a frontend prototype, not a live enquiry-delivery system.',
    role: 'Frontend development · Interaction & information design',
    stack: [
      'React',
      'TanStack Start',
      'Three.js',
      'TypeScript',
      'Tailwind CSS',
      'Cloudflare Workers',
    ],
    principle:
      'Use motion to explain the product. Never make the product information depend on the spectacle.',
    brief: [
      'I wanted PackCraft India to give packaging the attention it deserves. A carton has texture, structure and an opening sequence—qualities I could use to tell a more distinctive digital story. At the same time, I needed the website to answer the practical questions a buyer brings to a manufacturing business.',
      'I built a Three.js homepage experience and paired it with dedicated product, process and contact routes. The homepage introduces the brand through motion, while the supporting pages organise folding cartons, rigid boxes and corrugated shippers into clear families. I designed the two experiences to complement each other rather than compete.',
      'I built the 3D scene, scroll choreography, visual hierarchy and product-to-enquiry navigation. I treated motion as a way to explain the product, not a reason to hide the information. That principle shaped both the animation state and the readable pages a visitor can reach directly.',
    ],
    journeys: [
      {
        label: 'First impression',
        title: 'Turn the scroll into an unboxing.',
        body: 'I made the carton the central object in the homepage instead of using an unrelated animated background. I choreographed scroll progress into chapters for the company, product range, process and contact. Each chapter coordinates movement, opening and content reveal around the package. I separated the chapter windows from the sampled motion so I could reason about how the sequence enters, holds and exits a state. That gives the animation a clear narrative rhythm and makes the unboxing part of the information design.',
        detail: 'Scroll → carton motion → product and process chapters',
      },
      {
        label: 'Buyer research',
        title: 'Let the practical pages do the explaining.',
        body: 'I built dedicated pages for the information a buyer needs after the first impression. I grouped the product catalogue into folding cartons, rigid boxes and corrugated shippers, then used intended applications, structures, finishing choices and specification-style details to explain their differences. I paired that with a process page covering the path from brief to delivery. I keep this content in readable page structures, giving visitors a direct route to useful details without requiring them to watch the entire homepage sequence.',
        detail: 'Product family → specifications and process → enquiry context',
      },
      {
        label: 'Quote preparation',
        title: 'Carry the product interest into the enquiry.',
        body: 'I designed the quote journey to remember the product a visitor was exploring. I carry product type and intent through the contact route, then use the form to collect the information that shapes a packaging brief: quantity, timeline, company details and a description. I built the form interaction and acknowledgement state as part of the frontend experience. The design keeps the transition from comparison to enquiry purposeful, instead of asking someone to start again on a blank contact page after selecting a product family.',
        detail: 'Product intent → prefilled context → structured quote form',
      },
    ],
    gallery: [
      {
        file: 'homepage',
        alt: 'PackCraft India homepage showing a textured 3D carton, editorial typography and product exploration links',
        caption: 'The Three.js carton experience I built for the homepage.',
      },
      {
        file: 'products',
        alt: 'PackCraft India product comparison page for folding cartons, rigid boxes and corrugated packaging',
        caption:
          'Supporting product information — a readable route beyond the 3D introduction.',
      },
    ],
    architecture: [
      {
        title: 'Page & navigation layer',
        tools: 'React / TanStack Router',
        body: 'I use React and TanStack Router to connect the homepage with product, process and contact pages. I carry product intent into the quote interface through search parameters.',
      },
      {
        title: 'Motion & scene layer',
        tools: 'Three.js / sampled choreography',
        body: 'I separate package creation, chapter windows and motion sampling. I cap the pixel ratio on small screens, honour reduced-motion preferences, fall back to the plain page if WebGL is unavailable and dispose geometries and materials on teardown. I test active-package selection, chapter boundaries and resting poses to keep the choreography predictable.',
      },
      {
        title: 'Presentation & delivery',
        tools: 'Tailwind / Cloudflare Workers',
        body: 'I combine editorial typography, textured materials and responsive content layouts, with the application deployed on Cloudflare Workers. I keep product definitions organised in source data alongside the presentation.',
      },
    ],
    decisions: [
      {
        title: 'Make the motion meaningful.',
        constraint: 'Storytelling over decoration',
        body: 'I chose an opening carton because it gives the information a physical sequence: approach, open, discover and move on. I divided that sequence into chapter windows so the movement stays tied to the content being introduced. I wanted the interaction to explain something about packaging, not simply demonstrate a rendering effect.',
        outcome:
          'A product-led identity rather than an interchangeable animated hero.',
      },
      {
        title: 'Keep content outside the canvas.',
        constraint: 'Discovery still needs documents',
        body: 'I kept the product catalogue and process explanation in conventional routes so people can reach useful information directly. I use the homepage for discovery and identity, then let the supporting pages handle comparison and detail. That gives me clear internal links, readable content for search and a more practical experience on smaller screens.',
        outcome: 'A memorable entrance with practical pages behind it.',
      },
      {
        title: 'Test the choreography as state.',
        constraint: 'Beyond visual guesswork',
        body: 'I test the motion rules as well as designing the scene. I check that chapter windows meet correctly, that only one package is active at a progress point and that each package returns to its exact home pose outside its chapter. Those deterministic checks let me refine the choreography with a stable foundation underneath the visual work.',
        outcome:
          'Deterministic movement rules alongside the visual experience.',
      },
    ],
    delivered: [
      'I deployed PackCraft India at carton.suriyaprakash.in.',
      'I built an interactive Three.js carton sequence with narrative chapters.',
      'I created product comparison, process and contextual quote-interface pages.',
      'I tested the motion boundaries and connected product intent to the enquiry journey.',
    ],
    engineeringHighlights: [
      'I connect narrative chapters to deterministic package-motion states.',
      'I keep product and process information readable outside the 3D scene, and the page still works without WebGL or with reduced motion.',
      'I manage GPU cost with a mobile pixel-ratio cap and explicit resource disposal, and I preserve product intent into the quote-interface prototype.',
    ],
    fit: 'I build expressive websites for manufacturers and B2B businesses with products worth showing. I combine product-led interaction with clear information architecture, responsive page design and purposeful enquiry journeys. I would shape the visual story around your business and connect the delivery workflow to the systems your team uses.',
    questions: [
      [
        'How did I create the carton interaction?',
        'I built it with Three.js and coordinated the package behaviour with scroll progress. I modelled the movement as chapters and tested the state transitions, while keeping product and process information in dedicated readable pages. The scene respects reduced-motion settings, limits rendering cost on phones and degrades to the standard page when WebGL is unavailable.',
      ],
      [
        'How did I organise the product content?',
        'I grouped the range into product families, with structured definitions for applications, finishes and specifications. I maintain that content in source data and use it to build consistent comparison pages and contextual links into the quote interface.',
      ],
      [
        'What does the quote experience include?',
        'I built a frontend quote prototype with product-context handoff, brief fields and an acknowledgement state. For a business implementation, I can connect that interface to a reviewed email or CRM workflow as part of the agreed delivery scope.',
      ],
    ],
  },
}

export function projectStudyUrl(project: ProjectCaseStudy) {
  return `https://www.suriyaprakash.in/work/${project.slug}`
}

export function projectStudySchema(project: ProjectCaseStudy, author: string) {
  const url = projectStudyUrl(project)
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${url}#page`,
        url,
        name: project.title,
        description: project.description,
        isPartOf: { '@id': 'https://www.suriyaprakash.in/#website' },
        mainEntity: { '@id': `${url}#article` },
        breadcrumb: { '@id': `${url}#breadcrumbs` },
      },
      {
        '@type': 'Article',
        '@id': `${url}#article`,
        headline: `${project.name} — ${project.category}`,
        description: project.description,
        image: [
          `https://www.suriyaprakash.in/images/work/${project.slug}/homepage.jpg`,
        ],
        mainEntityOfPage: { '@id': `${url}#page` },
        inLanguage: 'en-IN',
        author: {
          '@type': 'Person',
          '@id': 'https://www.suriyaprakash.in/#person',
          name: author,
          url: 'https://www.suriyaprakash.in',
        },
        articleSection: 'Software development case study',
        about: project.category,
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumbs`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Portfolio',
            item: 'https://www.suriyaprakash.in/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Case studies',
            item: 'https://www.suriyaprakash.in/work',
          },
          { '@type': 'ListItem', position: 3, name: project.name, item: url },
        ],
      },
    ],
  }
}
